/**
 * System Email Templates Manager
 * Allows admin to edit all system emails using the block editor
 * Uses the EXACT SAME preview style as the Email Campaign editor
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  Mail, Save, RefreshCw, Edit, ChevronDown, ChevronUp,
  Building2, Shield, Key, UserPlus, Bell, Loader2, X,
  Monitor, Tablet, Smartphone, Copy, Power, PowerOff,
  History, CheckCircle, XCircle, Clock, Send, Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import EmailBlockBuilder from '@/components/EmailBlockBuilder';
import EmailBrandingSettings from '@/components/admin/EmailBrandingSettings';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Template categories and icons
const TEMPLATE_CATEGORIES = {
  engagement: { label: 'Engagement Emails', icon: Bell, color: '#22C55E' },
  system: { label: 'System Emails', icon: Shield, color: '#FF7901' },
  partnership: { label: 'Partnership Emails', icon: UserPlus, color: '#df1283' },
  b2b: { label: 'B2B Client Emails', icon: Building2, color: '#0FFFE2' }
};

const TEMPLATE_ICONS = {
  b2b_client_welcome: Building2,
  b2b_user_invite: UserPlus,
  password_reset_request: Key,
  admin_password_reset: Key,
  security_alert: Shield,
  notification: Bell,
  welcome: Mail,
  premium_welcome: Mail,
  first_entry_nudge: Bell,
  partner_onboarding: UserPlus,
  partnership_welcome: Mail,
  partnership_expiration: Bell
};

// Default names for templates that might be missing them
const TEMPLATE_NAMES = {
  welcome: 'User Welcome Email',
  premium_welcome: 'Premium Welcome Email',
  notification: 'Notification Email',
  b2b_client_welcome: 'B2B Client Welcome',
  b2b_user_invite: 'B2B User Invite',
  password_reset_request: 'Password Reset Request',
  admin_password_reset: 'Admin Password Reset',
  security_alert: 'Security Alert',
  first_entry_nudge: 'First Entry Nudge Email',
  partner_onboarding: 'Partner Onboarding',
  partnership_welcome: 'Partnership Welcome',
  partnership_expiration: 'Partnership Expiration'
};

const TEMPLATE_DESCRIPTIONS = {
  welcome: 'Sent to new users when they sign up',
  premium_welcome: 'Sent when a user upgrades to premium',
  notification: 'Template for system notifications',
  b2b_client_welcome: 'Sent to B2B client admins when their account is created',
  b2b_user_invite: 'Sent when a B2B client invites a user to their program',
  password_reset_request: 'Sent when a user requests to reset their password',
  admin_password_reset: 'Sent when an admin resets a user\'s password',
  security_alert: 'Sent for security events like account lockouts',
  first_entry_nudge: 'Sent to users who signed up but haven\'t created their first journal entry',
  partner_onboarding: 'Sent to partnership contacts with their access code and distribution instructions',
  partnership_welcome: 'Sent when a user redeems a partnership access code',
  partnership_expiration: 'Sent when a user\'s partnership access expires'
};

const TEMPLATE_CATEGORIES_MAP = {
  welcome: 'system',
  premium_welcome: 'system',
  notification: 'system',
  b2b_client_welcome: 'b2b',
  b2b_user_invite: 'b2b',
  password_reset_request: 'system',
  admin_password_reset: 'system',
  security_alert: 'system',
  first_entry_nudge: 'engagement',
  partner_onboarding: 'partnership',
  partnership_welcome: 'partnership',
  partnership_expiration: 'partnership'
};

const SystemEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [expandedCategories, setExpandedCategories] = useState(['engagement', 'partnership', 'b2b', 'system']);
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' or 'history'
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [togglingTemplate, setTogglingTemplate] = useState(null);
  const [testEmailModal, setTestEmailModal] = useState(null); // template_type to send test for
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // Form state for editing
  const [editForm, setEditForm] = useState({
    subject: '',
    body_html: ''
  });

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/email-templates`, {
        withCredentials: true
      });
      
      // Normalize templates - add missing names/categories
      const normalizedTemplates = (res.data.templates || []).map(t => ({
        ...t,
        name: t.name || TEMPLATE_NAMES[t.template_type] || t.template_type,
        description: t.description || TEMPLATE_DESCRIPTIONS[t.template_type] || '',
        category: t.category || TEMPLATE_CATEGORIES_MAP[t.template_type] || 'system',
        enabled: t.enabled !== false // Default to true
      }));
      
      setTemplates(normalizedTemplates);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDeliveryLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/email-templates/logs`, {
        withCredentials: true
      });
      setDeliveryLogs(res.data.logs || []);
      setStats(res.data.stats || null);
    } catch (err) {
      console.error('Failed to fetch delivery logs:', err);
      toast.error('Failed to load delivery history');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchDeliveryLogs();
    }
  }, [activeTab, fetchDeliveryLogs]);

  const handleToggleTemplate = async (template) => {
    const newEnabled = !template.enabled;
    setTogglingTemplate(template.template_type);
    
    try {
      await axios.post(
        `${API_BASE}/api/admin/email-templates/${template.template_type}/toggle?enabled=${newEnabled}`,
        {},
        { withCredentials: true }
      );
      
      setTemplates(prev => prev.map(t => 
        t.template_type === template.template_type 
          ? { ...t, enabled: newEnabled }
          : t
      ));
      
      toast.success(`${template.name} ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Failed to toggle template:', err);
      toast.error('Failed to update template status');
    } finally {
      setTogglingTemplate(null);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setEditForm({
      subject: template.subject || '',
      body_html: template.body_html || ''
    });
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE}/api/admin/email-templates/${editingTemplate.template_type}`,
        {
          subject: editForm.subject,
          body_html: editForm.body_html
        },
        { withCredentials: true }
      );
      
      toast.success(`${editingTemplate.name} template saved!`);
      
      // Update local state
      setTemplates(prev => prev.map(t => 
        t.template_type === editingTemplate.template_type 
          ? { ...t, subject: editForm.subject, body_html: editForm.body_html }
          : t
      ));
      
      setEditingTemplate(null);
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!editingTemplate) return;
    if (!window.confirm(`Reset "${editingTemplate.name}" to its default content? This will overwrite your current changes.`)) {
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(
        `${API_BASE}/api/admin/email-templates/${editingTemplate.template_type}/reset`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`${editingTemplate.name} reset to default!`);
      
      // Refresh templates to get the updated content
      await fetchTemplates();
      setEditingTemplate(null);
    } catch (err) {
      console.error('Failed to reset template:', err);
      toast.error('Failed to reset template');
    } finally {
      setResetting(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const sendTestEmail = async () => {
    if (!testEmailModal || !testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSendingTest(true);
      const res = await axios.post(
        `${API_BASE}/api/admin/email-templates/${testEmailModal}/send-test`,
        {
          recipient_email: testEmailAddress,
          recipient_name: 'Test User'
        },
        { withCredentials: true }
      );
      
      toast.success(`Test email sent to ${testEmailAddress}!`);
      setTestEmailModal(null);
      setTestEmailAddress('');
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // Group templates by category
  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.category || 'system';
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {});

  // Render the EXACT same preview as Email Campaign
  const renderEmailPreview = () => {
    const bodyHtml = editForm.body_html || '';
    // Strip the BLOCKS comment for display
    const cleanHtml = bodyHtml.replace(/<!-- BLOCKS:.*? -->\n?/g, '');
    
    // Replace merge tags with sample data for preview
    const previewHtml = cleanHtml
      .replace(/\{\{first_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">John</span>')
      .replace(/\{\{email\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">john@example.com</span>')
      .replace(/\{\{company_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">Acme Corp</span>')
      .replace(/\{\{app_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">Journal</span>')
      .replace(/\{\{portal_url\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">https://example.com/client-portal</span>')
      .replace(/\{\{admin_username\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">acme-admin</span>')
      .replace(/\{\{portal_password\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">••••••••</span>')
      .replace(/\{\{total_licenses\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">50</span>')
      .replace(/\{\{client_name\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">Acme Corp</span>')
      .replace(/\{\{invite_url\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">https://example.com/invite/abc123</span>')
      .replace(/\{\{reset_url\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">https://example.com/reset/xyz789</span>')
      .replace(/\{\{new_password\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">TempPass123!</span>')
      .replace(/\{\{login_url\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">https://example.com/login</span>')
      .replace(/\{\{event_type\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">Account Locked</span>')
      .replace(/\{\{identifier\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">user@example.com</span>')
      .replace(/\{\{timestamp\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">Apr 3, 2026 2:30 PM</span>')
      .replace(/\{\{details\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">Multiple failed login attempts detected</span>')
      .replace(/\{\{admin_url\}\}/g, '<span style="background:#fef3c7;padding:0 2px;border-radius:2px;">https://example.com/admin</span>')
      .replace(/\{\{primary_color\}\}/g, '#df1283');
    
    return (
      <div className={`mx-auto transition-all duration-300 ${
        previewDevice === 'mobile' ? 'max-w-[320px]' : 
        previewDevice === 'tablet' ? 'max-w-[400px]' : 
        'max-w-full'
      }`}>
        {/* Phone/Tablet Frame for mobile/tablet */}
        {previewDevice !== 'desktop' && (
          <div className="bg-gray-800 rounded-[2rem] p-2 shadow-xl">
            <div className="bg-gray-800 rounded-[1.5rem] overflow-hidden">
              {/* Notch for mobile */}
              {previewDevice === 'mobile' && (
                <div className="h-6 bg-gray-800 flex items-center justify-center">
                  <div className="w-20 h-4 bg-black rounded-full"></div>
                </div>
              )}
              
              {/* Email Preview Card */}
              <div className="bg-white overflow-hidden">
                {/* Preview Header */}
                <div className="bg-gray-50 px-3 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-[8px] font-bold">
                      UJ
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-xs truncate">UnCloned® Journal</p>
                      <p className="text-[10px] text-gray-500 truncate">info+journal@uncloned...</p>
                    </div>
                  </div>
                </div>
                
                {/* Subject Preview */}
                <div className="px-3 py-2 border-b bg-white">
                  <p className="font-semibold text-gray-900 text-xs truncate">
                    {editForm.subject || 'Your subject line...'}
                  </p>
                </div>
                
                {/* Body Preview */}
                <div className="p-3 max-h-[400px] overflow-y-auto">
                  {previewHtml ? (
                    <div 
                      className="max-w-none text-gray-700 email-preview-content"
                      style={{ fontSize: previewDevice === 'mobile' ? '12px' : '13px', lineHeight: '1.5' }}
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(`<style>
                          .email-preview-content p { margin: 0.4em 0; }
                          .email-preview-content div { margin: 0.2em 0; }
                          .email-preview-content img { max-width: 100%; height: auto; }
                          .email-preview-content ul, .email-preview-content ol { padding-left: 1.2em; margin: 0.4em 0; }
                          .email-preview-content li { margin: 0.2em 0; }
                          .email-preview-content a { color: #0FFFE2; }
                        </style>` + previewHtml)
                      }}
                    />
                  ) : (
                    <p className="text-gray-400 italic text-xs">Your email content...</p>
                  )}
                </div>
                
                {/* Preview Footer */}
                <div className="px-3 py-2 bg-gray-50 border-t text-center">
                  <p className="text-[10px] text-gray-400">Sent with love from UnCloned®</p>
                </div>
              </div>
              
              {/* Home indicator for mobile */}
              {previewDevice === 'mobile' && (
                <div className="h-5 bg-gray-800 flex items-center justify-center">
                  <div className="w-24 h-1 bg-white rounded-full"></div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Desktop Preview */}
        {previewDevice === 'desktop' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Preview Header */}
            <div className="bg-gray-50 px-4 py-3 border-b">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center text-white text-xs font-bold">
                  UJ
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">UnCloned® Journal</p>
                  <p className="text-xs text-gray-500 truncate">info+journal@unclonedmarketing.com</p>
                </div>
              </div>
            </div>
            
            {/* Subject Preview */}
            <div className="px-4 py-3 border-b bg-white">
              <p className="font-semibold text-gray-900 truncate">
                {editForm.subject || 'Your subject line here...'}
              </p>
            </div>
            
            {/* Body Preview */}
            <div className="p-4 min-h-[300px] max-h-[500px] overflow-y-auto">
              {previewHtml ? (
                <div 
                  className="max-w-none text-gray-700 email-preview-content"
                  style={{ fontSize: '14px', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(`<style>
                      .email-preview-content p { margin: 0.5em 0; }
                      .email-preview-content div { margin: 0.25em 0; }
                      .email-preview-content img { max-width: 100%; height: auto; }
                      .email-preview-content ul, .email-preview-content ol { padding-left: 1.5em; margin: 0.5em 0; }
                      .email-preview-content li { margin: 0.25em 0; }
                      .email-preview-content a { color: #0FFFE2; }
                    </style>` + previewHtml)
                  }}
                />
              ) : (
                <p className="text-gray-400 italic">Start writing your email content...</p>
              )}
            </div>
            
            {/* Preview Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t text-center">
              <p className="text-xs text-gray-400">Sent with love from UnCloned® • © 2026 UnCloned®. All rights reserved.</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#df1283]" />
            System Email Templates
          </h2>
          <p className="text-sm text-[#FDF2F8]/50 mt-1">
            Customize all automated system emails with the block editor
          </p>
        </div>
        <Button
          onClick={activeTab === 'templates' ? fetchTemplates : fetchDeliveryLogs}
          variant="outline"
          className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-2 bg-[#1a0527]/50 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'templates'
              ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white'
              : 'text-[#FDF2F8]/70 hover:text-white'
          }`}
        >
          <Mail className="w-4 h-4" />
          Templates
        </button>
        <button
          onClick={() => setActiveTab('branding')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'branding'
              ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white'
              : 'text-[#FDF2F8]/70 hover:text-white'
          }`}
          data-testid="email-branding-tab"
        >
          <Palette className="w-4 h-4" />
          Branding
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'history'
              ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white'
              : 'text-[#FDF2F8]/70 hover:text-white'
          }`}
        >
          <History className="w-4 h-4" />
          Delivery History
        </button>
      </div>

      {/* Stats Summary */}
      {stats && activeTab === 'history' && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_sent}</p>
                <p className="text-sm text-[#FDF2F8]/50">Emails Sent</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_failed}</p>
                <p className="text-sm text-[#FDF2F8]/50">Failed</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <PowerOff className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_disabled}</p>
                <p className="text-sm text-[#FDF2F8]/50">Skipped (Disabled)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery History Tab */}
      {activeTab === 'history' && (
        <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#4A0A66]">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Send className="w-5 h-5 text-[#df1283]" />
              Recent Email Deliveries
            </h3>
          </div>
          
          {logsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#df1283]" />
            </div>
          ) : deliveryLogs.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-[#FDF2F8]/20 mx-auto mb-3" />
              <p className="text-[#FDF2F8]/50">No emails sent yet</p>
            </div>
          ) : (
            <div className="divide-y divide-[#4A0A66]/30">
              {deliveryLogs.map((log) => (
                <div key={log._id || log.id || `log-${log.sent_at}`} className="px-6 py-4 hover:bg-[#4A0A66]/10 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        log.status === 'sent' ? 'bg-green-500/20' :
                        log.status === 'failed' ? 'bg-red-500/20' :
                        'bg-yellow-500/20'
                      }`}>
                        {log.status === 'sent' ? <CheckCircle className="w-4 h-4 text-green-400" /> :
                         log.status === 'failed' ? <XCircle className="w-4 h-4 text-red-400" /> :
                         <PowerOff className="w-4 h-4 text-yellow-400" />}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {TEMPLATE_NAMES[log.template_type] || log.template_type}
                        </p>
                        <p className="text-sm text-[#FDF2F8]/50">
                          To: {log.recipient_email}
                          {log.recipient_name && ` (${log.recipient_name})`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        log.status === 'sent' ? 'bg-green-500/20 text-green-400' :
                        log.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {log.status}
                      </span>
                      <p className="text-xs text-[#FDF2F8]/40 mt-1">
                        {log.sent_at ? format(new Date(log.sent_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {log.error_message && (
                    <p className="text-xs text-red-400 mt-2 bg-red-500/10 px-3 py-2 rounded">
                      Error: {log.error_message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
        {Object.entries(TEMPLATE_CATEGORIES).map(([categoryKey, categoryInfo]) => {
          const categoryTemplates = groupedTemplates[categoryKey] || [];
          if (categoryTemplates.length === 0) return null;
          
          const CategoryIcon = categoryInfo.icon;
          const isExpanded = expandedCategories.includes(categoryKey);
          
          return (
            <div key={categoryKey} className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(categoryKey)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#4A0A66]/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CategoryIcon className="w-5 h-5" style={{ color: categoryInfo.color }} />
                  <span className="text-white font-semibold">{categoryInfo.label}</span>
                  <span className="text-[#FDF2F8]/50 text-sm">({categoryTemplates.length})</span>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-[#FDF2F8]/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#FDF2F8]/50" />
                )}
              </button>
              
              {/* Templates List */}
              {isExpanded && (
                <div className="border-t border-[#4A0A66]/50">
                  {categoryTemplates.map((template) => {
                    const TemplateIcon = TEMPLATE_ICONS[template.template_type] || Mail;
                    
                    return (
                      <div
                        key={template.template_type}
                        className={`px-6 py-4 border-b border-[#4A0A66]/30 last:border-b-0 hover:bg-[#4A0A66]/10 transition-colors ${!template.enabled ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${template.enabled ? 'bg-[#4A0A66]/30' : 'bg-gray-600/30'}`}>
                              <TemplateIcon className={`w-5 h-5 ${template.enabled ? 'text-[#df1283]' : 'text-gray-500'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-white font-medium">{template.name}</h3>
                                {!template.enabled && (
                                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                                    Disabled
                                  </span>
                                )}
                              </div>
                              <p className="text-[#FDF2F8]/50 text-sm">{template.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Toggle Switch */}
                            <button
                              onClick={() => handleToggleTemplate(template)}
                              disabled={togglingTemplate === template.template_type}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                template.enabled ? 'bg-green-500' : 'bg-gray-600'
                              }`}
                              title={template.enabled ? 'Click to disable' : 'Click to enable'}
                            >
                              {togglingTemplate === template.template_type ? (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                                </span>
                              ) : (
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    template.enabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              )}
                            </button>
                            <Button
                              onClick={() => window.open(`${API_BASE}/api/admin/email-templates/${template.template_type}/preview`, '_blank')}
                              size="sm"
                              variant="outline"
                              className="border-[#0FFFE2]/50 text-[#0FFFE2] hover:bg-[#0FFFE2]/10"
                              title="Preview how this email looks when sent"
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              onClick={() => setTestEmailModal(template.template_type)}
                              size="sm"
                              variant="outline"
                              className="border-[#FF7901]/50 text-[#FF7901] hover:bg-[#FF7901]/10"
                              title="Send a test email to verify in your inbox"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Test
                            </Button>
                            <Button
                              onClick={() => handleEditTemplate(template)}
                              size="sm"
                              className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </div>
                        
                        {/* Merge Tags Preview */}
                        {template.merge_tags && template.merge_tags.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="text-[#FDF2F8]/40 text-xs">Available tags:</span>
                            {template.merge_tags.map(tag => (
                              <code 
                                key={tag}
                                className="text-xs bg-[#0FFFE2]/10 text-[#0FFFE2] px-2 py-0.5 rounded"
                              >
                                {`{{${tag}}}`}
                              </code>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <EmailBrandingSettings />
      )}

      {/* Edit Modal - Uses EXACT same layout as Email Campaign */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl w-full max-w-7xl my-8 border border-[#4A0A66] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-[#df1283]" />
                <div>
                  <h2 className="text-xl font-bold text-white">{editingTemplate.name}</h2>
                  <p className="text-sm text-[#FDF2F8]/50">{editingTemplate.description}</p>
                </div>
              </div>
              <button
                onClick={() => setEditingTemplate(null)}
                className="p-2 hover:bg-[#4A0A66]/30 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#FDF2F8]/70" />
              </button>
            </div>
            
            {/* Modal Content - Two Column Layout like Email Campaign */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
              {/* Left Column - Editor */}
              <div className="space-y-4">
                {/* Subject Line */}
                <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                  <label className="block text-sm font-medium text-white mb-2">Subject Line</label>
                  <Input
                    value={editForm.subject}
                    onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Email subject..."
                    className="bg-[#4A0A66]/30 border-[#4A0A66] text-white placeholder:text-[#FDF2F8]/30"
                  />
                </div>
                
                {/* Merge Tags */}
                {editingTemplate.merge_tags && editingTemplate.merge_tags.length > 0 && (
                  <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                    <label className="block text-sm font-medium text-white mb-2">Available Merge Tags</label>
                    <p className="text-xs text-[#FDF2F8]/40 mb-3">Click to copy, then paste into your email</p>
                    <div className="flex flex-wrap gap-2">
                      {editingTemplate.merge_tags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${tag}}}`);
                            toast.success(`Copied {{${tag}}}`);
                          }}
                          className="text-xs bg-[#0FFFE2]/10 text-[#0FFFE2] px-3 py-1.5 rounded hover:bg-[#0FFFE2]/20 transition-colors flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          {`{{${tag}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Email Content - Block Editor */}
                <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                  <label className="block text-sm font-medium text-white mb-2">Email Content</label>
                  <p className="text-xs text-[#FDF2F8]/40 mb-3">
                    Use the block editor to design your email
                  </p>
                  <div className="bg-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
                    <EmailBlockBuilder
                      value={editForm.body_html}
                      onChange={(html) => setEditForm(prev => ({ ...prev, body_html: html }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Right Column - Preview (EXACT same as Email Campaign) */}
              <div className="space-y-4">
                <div className="bg-[#1a0527]/50 rounded-xl p-4 border border-[#4A0A66]/30">
                  {/* Preview Header with Device Toggle */}
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-medium text-white">LIVE PREVIEW</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => window.open(`${API_BASE}/api/admin/email-templates/${editingTemplate.template_type}/preview`, '_blank')}
                        className="text-xs bg-[#0FFFE2]/20 text-[#0FFFE2] px-3 py-1.5 rounded hover:bg-[#0FFFE2]/30 transition-colors flex items-center gap-1"
                        title="View exactly how this email will look when sent"
                      >
                        <Mail className="w-3 h-3" />
                        View Full Email
                      </button>
                      <button
                        onClick={() => setTestEmailModal(editingTemplate.template_type)}
                        className="text-xs bg-[#FF7901]/20 text-[#FF7901] px-3 py-1.5 rounded hover:bg-[#FF7901]/30 transition-colors flex items-center gap-1"
                        title="Send a test email to your inbox"
                      >
                        <Send className="w-3 h-3" />
                        Send Test
                      </button>
                      <div className="flex items-center gap-1 bg-[#4A0A66]/30 rounded-lg p-1">
                        <button
                          onClick={() => setPreviewDevice('mobile')}
                          className={`p-2 rounded transition-colors ${previewDevice === 'mobile' ? 'bg-[#df1283] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                          title="Mobile Preview"
                        >
                          <Smartphone className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setPreviewDevice('tablet')}
                          className={`p-2 rounded transition-colors ${previewDevice === 'tablet' ? 'bg-[#df1283] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                          title="Tablet Preview"
                        >
                          <Tablet className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setPreviewDevice('desktop')}
                          className={`p-2 rounded transition-colors ${previewDevice === 'desktop' ? 'bg-[#df1283] text-white' : 'text-[#FDF2F8]/50 hover:text-white'}`}
                          title="Desktop Preview"
                        >
                          <Monitor className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview Container */}
                  <div className="bg-[#4A0A66]/20 rounded-lg p-4 min-h-[500px]">
                    {renderEmailPreview()}
                  </div>
                  
                  <p className="text-xs text-[#FDF2F8]/40 text-center mt-3">
                    This is a content preview. Click "View Full Email" to see the complete email with header, logo, and footer.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#4A0A66]">
              <Button
                onClick={handleResetToDefault}
                disabled={resetting}
                variant="outline"
                className="border-[#FF7901] text-[#FF7901] hover:bg-[#FF7901]/10"
              >
                {resetting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset to Default
                  </>
                )}
              </Button>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setEditingTemplate(null)}
                  variant="outline"
                  className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Template
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Email Modal */}
      {testEmailModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl w-full max-w-md border border-[#4A0A66] shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]">
              <div className="flex items-center gap-3">
                <Send className="w-5 h-5 text-[#FF7901]" />
                <h2 className="text-lg font-bold text-white">Send Test Email</h2>
              </div>
              <button
                onClick={() => { setTestEmailModal(null); setTestEmailAddress(''); }}
                className="p-2 hover:bg-[#4A0A66]/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#FDF2F8]/50" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#FDF2F8]/70">
                Send a test email to verify how the <strong className="text-[#0FFFE2]">{TEMPLATE_NAMES[testEmailModal] || testEmailModal}</strong> template looks in an actual inbox.
              </p>
              
              <div>
                <label className="block text-sm text-[#FDF2F8]/50 mb-2">Recipient Email *</label>
                <Input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="your-email@example.com"
                  className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
                  autoFocus
                />
              </div>
              
              <p className="text-xs text-[#FDF2F8]/40">
                The test email will include "[TEST]" in the subject line and use sample data for all merge tags.
              </p>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setTestEmailModal(null); setTestEmailAddress(''); }}
                  className="border-[#4A0A66] text-[#FDF2F8]/70"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendTestEmail}
                  disabled={!testEmailAddress || sendingTest}
                  className="bg-gradient-to-r from-[#FF7901] to-[#df1283] hover:opacity-90"
                >
                  {sendingTest ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Test Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemEmailTemplates;
